import React, { useState, useEffect, useCallback } from 'react';
import { styles, colors } from '../../styles/globalStyles';

const STATUS_LABEL = {
  pendente: 'Pendente',
  validado: 'Validado',
  expirado: 'Expirado',
  erro: 'Erro',
};

const formatarHora = (d) => d ? new Date(d).toLocaleString('pt-BR') : '—';

export const CheckinsTotalPass = ({ createApi, isMobile = false }) => {
  const [checkins, setCheckins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sucesso, setSucesso] = useState('');
  const [confirmando, setConfirmando] = useState({});
  const [filtroStatus, setFiltroStatus] = useState('pendente');

  const mostrarSucesso = (msg) => {
    setSucesso(msg);
    setTimeout(() => setSucesso(''), 3000);
  };

  const carregar = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const api = createApi();
      const response = await api.get(`/admin/totalpass/checkins?status=${filtroStatus}`);
      setCheckins(response.data);
    } catch {
      setError('Erro ao carregar check-ins');
    } finally {
      setLoading(false);
    }
  }, [createApi, filtroStatus]);

  useEffect(() => { carregar(); }, [carregar]);

  const confirmar = async (checkin) => {
    setConfirmando(prev => ({ ...prev, [checkin.id]: true }));
    setError('');
    try {
      const api = createApi();
      await api.post(`/admin/totalpass/checkins/${checkin.id}/validar`);
      setCheckins(prev => prev.filter(c => c.id !== checkin.id));
      mostrarSucesso(`Check-in de ${checkin.aluno_nome || checkin.tp_user_nome || 'aluno'} confirmado!`);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao confirmar check-in');
      // Se o backend já resolveu o status (já validado/expirado/erro), tira da lista de pendentes
      if ([404, 409, 422].includes(err.response?.status)) {
        setCheckins(prev => prev.filter(c => c.id !== checkin.id));
      }
    } finally {
      setConfirmando(prev => { const n = { ...prev }; delete n[checkin.id]; return n; });
    }
  };

  const nomeExibicao = (c) => c.aluno_nome || c.tp_user_nome || 'Não identificado';

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <h3 style={{ margin: 0, color: colors.dark }}>Check-ins TotalPass</h3>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <select
            value={filtroStatus}
            onChange={(e) => setFiltroStatus(e.target.value)}
            style={{ ...styles.formInput, cursor: 'pointer' }}
          >
            <option value="pendente">Pendentes</option>
            <option value="validado">Validados</option>
            <option value="expirado">Expirados</option>
            <option value="erro">Erro</option>
          </select>
          <button onClick={carregar} disabled={loading} style={{ ...styles.button, ...styles.buttonOutline, ...styles.buttonSmall }}>
            {loading ? '...' : '🔄 Atualizar'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ ...styles.alert, ...styles.alertError, marginBottom: '1rem' }}>
          {error}
          <button onClick={() => setError('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 'bold' }}>✕</button>
        </div>
      )}
      {sucesso && (
        <div style={{ ...styles.alert, ...styles.alertSuccess, marginBottom: '1rem' }}>{sucesso}</div>
      )}

      <div style={styles.card}>
        {loading && checkins.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div style={styles.spinner}></div>
            <p>Carregando...</p>
          </div>
        ) : checkins.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: colors.gray }}>
            Nenhum check-in {(STATUS_LABEL[filtroStatus] || filtroStatus).toLowerCase()}
          </div>
        ) : isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {checkins.map(c => (
              <div key={c.id} style={{
                background: c.aluno_id ? '#f8fafc' : '#fff7ed',
                border: `1px solid ${c.aluno_id ? '#e2e8f0' : '#fed7aa'}`,
                borderRadius: '10px', padding: '0.875rem',
              }}>
                <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#1e293b' }}>{nomeExibicao(c)}</div>
                {!c.aluno_id && <div style={{ fontSize: '0.7rem', color: '#c2410c', fontWeight: 700 }}>NÃO IDENTIFICADO</div>}
                <div style={{ fontSize: '0.78rem', color: colors.gray }}>{c.tp_user_email || c.tp_user_telefone || ''}</div>
                <div style={{ fontSize: '0.75rem', color: colors.gray, marginTop: '0.35rem' }}>
                  Plano: {c.plan_code || '—'} · Local: {c.place_nome || '—'}
                </div>
                <div style={{ fontSize: '0.75rem', color: colors.gray }}>
                  Início: {formatarHora(c.started_at)} · Expira: {formatarHora(c.expires_at)}
                </div>
                {c.status === 'pendente' ? (
                  <button
                    onClick={() => confirmar(c)}
                    disabled={!!confirmando[c.id]}
                    style={{ ...styles.button, ...styles.buttonPrimary, ...styles.buttonSmall, marginTop: '0.6rem', width: '100%' }}
                  >
                    {confirmando[c.id] ? 'Confirmando...' : '✓ Confirmar check-in'}
                  </button>
                ) : (
                  <div style={{ marginTop: '0.5rem' }}>
                    <span style={{ ...styles.badge, ...(c.status === 'validado' ? styles.badgeSuccess : styles.badgeDanger), fontSize: '0.7rem' }}>
                      {STATUS_LABEL[c.status] || c.status}
                    </span>
                    {c.erro_mensagem && <div style={{ fontSize: '0.72rem', color: colors.gray, marginTop: '0.25rem' }}>{c.erro_mensagem}</div>}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: colors.grayLight }}>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: `1px solid ${colors.gray}` }}>Aluno</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: `1px solid ${colors.gray}` }}>Plano / Local</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: `1px solid ${colors.gray}` }}>Início</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: `1px solid ${colors.gray}` }}>Expira às</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: `1px solid ${colors.gray}` }}>Ação / Status</th>
                </tr>
              </thead>
              <tbody>
                {checkins.map(c => (
                  <tr key={c.id} style={{ borderBottom: `1px solid ${colors.grayLight}` }}>
                    <td style={{ padding: '0.75rem' }}>
                      <div style={{ fontWeight: 600 }}>{nomeExibicao(c)}</div>
                      {!c.aluno_id && <div style={{ fontSize: '0.65rem', color: '#c2410c', fontWeight: 700 }}>NÃO IDENTIFICADO</div>}
                      <div style={{ fontSize: '0.8rem', color: colors.gray }}>{c.tp_user_email || c.tp_user_telefone || ''}</div>
                    </td>
                    <td style={{ padding: '0.75rem', fontSize: '0.85rem', color: colors.gray }}>{c.plan_code || '—'} · {c.place_nome || '—'}</td>
                    <td style={{ padding: '0.75rem', fontSize: '0.85rem', color: colors.gray }}>{formatarHora(c.started_at)}</td>
                    <td style={{ padding: '0.75rem', fontSize: '0.85rem', color: colors.gray }}>{formatarHora(c.expires_at)}</td>
                    <td style={{ padding: '0.75rem' }}>
                      {c.status === 'pendente' ? (
                        <button
                          onClick={() => confirmar(c)}
                          disabled={!!confirmando[c.id]}
                          style={{ ...styles.button, ...styles.buttonPrimary, ...styles.buttonSmall }}
                        >
                          {confirmando[c.id] ? 'Confirmando...' : '✓ Confirmar'}
                        </button>
                      ) : (
                        <>
                          <span style={{ ...styles.badge, ...(c.status === 'validado' ? styles.badgeSuccess : styles.badgeDanger), fontSize: '0.75rem' }}>
                            {STATUS_LABEL[c.status] || c.status}
                          </span>
                          {c.erro_mensagem && <div style={{ fontSize: '0.72rem', color: colors.gray, marginTop: '0.25rem' }}>{c.erro_mensagem}</div>}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
